import React, { useEffect, useMemo, useRef, useState } from 'react';
import './style.scss';
import useUserData from '../../contexts/userData';
import useWeb3, { MetamaskStatuses } from '../../contexts/web3';
import HeaderLogo from './logo/HeaderLogo';
import MenuButton from './menuButton/MenuButton';
import SocialButtons from '../general/socialIcons';
import LoginButton from './loginButton';
import HeaderProfileBlock from './profileBlock';
import clsx from 'clsx';
import useInitFight, { FightPageModeMarkers } from '../../contexts/initFight';
import BalanceBlock from './balance';
import AppHeaderImproperNetwork from './improperNetwork';
import { useLocation } from 'react-router-dom';
import HeaderBanner from '../headerBanner';
import useAlert from '../../contexts/alert';

const useScroll = (
  callback: (prevValue: number, newValue: number, totalHeight: number) => void
) => {
  const prevValue = useRef(window.scrollY || 0);

  useEffect(() => {
    const handleScroll = () => {
      const body = document.body;
      const html = document.documentElement;
      const totalHeight =
        Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        ) - window.innerHeight;
      callback(prevValue.current, window.scrollY, totalHeight);
      prevValue.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

type LocationData = {
  isFight: boolean;
  isLanding: boolean;
};

interface Props {
  menuOpened: boolean;
  closeMenu: () => void;
  switchMenu: () => void;
}

const AppHeader = ({ menuOpened, switchMenu, closeMenu }: Props) => {
  const alert = useAlert();
  const location = useLocation();
  const { chainData } = useWeb3();
  const { loggedIn } = useUserData();
  const { mode, goHome } = useInitFight();
  const [headerVisible, setHeaderVisible] = useState(true);

  useScroll((prevValue, newValue, total) => {
    setHeaderVisible(
      (newValue < prevValue && newValue < total) || newValue <= 0
    );
  });

  const handleLogoClick = () => {
    if (mode.marker === FightPageModeMarkers.RESULTS) {
      goHome();
    }
    closeMenu();
  };

  const { isFight, isLanding } = useMemo<LocationData>(() => {
    if (!loggedIn || location.pathname === '/about') {
      return { isFight: false, isLanding: true };
    }
    if (
      location.pathname === '/' &&
      mode.marker === FightPageModeMarkers.BATTLE
    ) {
      return { isFight: true, isLanding: false };
    }
    return { isFight: false, isLanding: false };
  }, [location.pathname, mode.marker, loggedIn]);

  const copyCode = () => {
    try {
      navigator.clipboard.writeText('GENESIS50');
      alert.show('Copied to clipboard');
    } catch (err) {
      alert.showError(`Can't copy the code. Please copy manually`);
    }
  };

  return (
    <div className='app-header-wrapper'>
      {isLanding && process.env.REACT_APP_BANNER_ENABLED && (
        <HeaderBanner transparent={!headerVisible}>
          Enjoy mint with 50% secret code:{' '}
          <b onClick={copyCode} style={{ cursor: 'pointer' }}>
            GENESIS50
          </b>
          . Limited offer.
        </HeaderBanner>
      )}
      <div
        className={clsx(
          'app-header',
          !isFight && (headerVisible || menuOpened) && 'app-header-visible',
          menuOpened && 'app-header-inverted'
        )}
      >
        <div className='app-header-part'>
          <HeaderLogo inverted={menuOpened} onClick={handleLogoClick} />
        </div>
        {chainData.status === MetamaskStatuses.WRONG_CHAIN ? (
          <ImproperNetwork name={chainData.chainName} inverted={menuOpened} />
        ) : (
          <NormalContent
            loggedIn={loggedIn}
            closeMenu={closeMenu}
            switchMenu={switchMenu}
            menuOpened={menuOpened}
          />
        )}
        <div className='app-header-pseudo-shadow' />
        <div className='app-header-pseudo-shadow app-header-pseudo-shadow-inverted' />
      </div>
    </div>
  );
};

interface ContentProps {
  menuOpened: boolean;
  loggedIn: boolean;
  closeMenu: () => void;
  switchMenu: () => void;
}

const NormalContent = ({
  menuOpened,
  switchMenu,
  closeMenu,
  loggedIn
}: ContentProps) => (
  <div className='app-header-part'>
    <SocialButtons
      inverted={menuOpened}
      className={clsx('app-header-socials', loggedIn && 'logged-in')}
    />
    {loggedIn ? (
      <LoggedIn inverted={menuOpened} closeMenu={closeMenu} />
    ) : (
      <LoggedOut inverted={menuOpened} />
    )}
    <MenuButton
      active={menuOpened}
      onClick={switchMenu}
      className={clsx(!loggedIn && 'app-header-menu-button-out')}
    />
  </div>
);

interface LoggedInProps {
  inverted: boolean;
  closeMenu: () => void;
}

const LoggedIn = ({ inverted, closeMenu }: LoggedInProps) => (
  <>
    <BalanceBlock
      inverted={inverted}
      className='app-header-balance'
      onClick={closeMenu}
    />
    <HeaderProfileBlock
      inverted={inverted}
      closeMenu={closeMenu}
      className='app-header-profile'
    />
  </>
);

interface LoggedOutProps {
  inverted: boolean;
}

const LoggedOut = ({ inverted }: LoggedOutProps) => (
  <LoginButton inverted={inverted} className='app-header-login' />
);

interface ImproperProps {
  name: string;
  inverted: boolean;
}

const ImproperNetwork = ({ name, inverted }: ImproperProps) => (
  <AppHeaderImproperNetwork
    name={name}
    inverted={inverted}
    className='app-header-improper-network'
  />
);

export default AppHeader;
